import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Stack } from '@mui/material';
import { getCustomerCallSnapshot } from 'shared/common.api';
import { CustomerDrawerContext } from 'context/customer-drawer-context';
import { RealtimeContext } from 'context/realtime-context';
import { SoftphoneContext } from 'context/softphone-context';
import { UserStateContext } from 'context/user-state-context';
import { CallPop } from './call-pop.component';

// Same layer as the softphone panel: above the app chrome, below modals.
const Z_INDEX = 850;

// A card from a backend event alone has no ringing state to follow, so it times out.
const EVENT_CARD_MS = 30000;

/** Compare numbers across sources: the SDK and the backend don't agree on formatting. */
const phoneKey = phone => `${phone || ''}`.replace(/\D/g, '').slice(-10);

/**
 * Decides which incoming calls get a call pop, and what its buttons do.
 *
 * Calls reach the browser two ways, and a card can start from either:
 *   - the softphone (when connected here) rings the moment the call arrives, and is the
 *     only thing that can answer it - so it decides when its cards come and go.
 *   - the backend's `voice_call.incoming` event arrives once the webhook is processed and
 *     carries the matched customer. It attaches that customer to the ringing card - by the
 *     DialStack call id when it can, by number otherwise - or, with nothing ringing here,
 *     becomes a card of its own that can't be answered.
 */
export const CallPopHost = () => {
  const {
    asCompany
  } = useContext(UserStateContext);
  const {
    subscribe
  } = useContext(RealtimeContext) || {};
  const {
    openCustomerDrawer
  } = useContext(CustomerDrawerContext);
  const {
    incomingCalls = [],
    answerCall,
    declineCall,
    open: openSoftphone,
    isOpen: softphoneOpen
  } = useContext(SoftphoneContext) || {};

  // Newest last. { key, callId, callerPhone, callerName, customerId }. callId is set only
  // while the card is backed by a call ringing on this softphone.
  const [cards, setCards] = useState([]);
  // customerId -> { data, loading }
  const [snapshots, setSnapshots] = useState({});
  // phoneKey -> true while a card without a customer is looking its caller up by number.
  const [phoneLookups, setPhoneLookups] = useState({});
  const timers = useRef({});
  const requested = useRef(new Set());
  const lookedUp = useRef(new Set());
  const removeCard = useCallback(key => {
    clearTimeout(timers.current[key]);
    delete timers.current[key];
    setCards(current => current.filter(card => card.key !== key));
  }, []);
  useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

  // The softphone owns the lifetime of its cards: one per ringing call, gone once it stops.
  useEffect(() => {
    const ringing = new Set(incomingCalls.map(call => call.id));
    setCards(current => {
      let next = current.filter(card => !card.callId || ringing.has(card.callId));
      incomingCalls.forEach(call => {
        if (next.some(card => card.callId === call.id)) {
          return;
        }

        // The event may have beaten the ring here, in which case its card takes the call on.
        const early = next.find(card => !card.callId && phoneKey(card.callerPhone) === phoneKey(call.from));
        if (early) {
          clearTimeout(timers.current[early.key]);
          delete timers.current[early.key];
          next = next.map(card => card === early ? {
            ...card,
            callId: call.id,
            callerName: card.callerName || call.fromName
          } : card);
        } else {
          next = [...next, {
            key: call.id,
            callId: call.id,
            callerPhone: call.from,
            callerName: call.fromName,
            customerId: null
          }];
        }
      });
      return next;
    });
  }, [incomingCalls]);
  useEffect(() => {
    if (!subscribe) {
      return undefined;
    }
    return subscribe('voice_call.incoming', (payload, event) => {
      // Same rule as the toast: the channel is per user, and a user can span companies.
      if (payload.company_id && payload.company_id !== asCompany?.id) {
        return;
      }

      // Outgoing calls keep the plain toast; see realtime-context.
      if (payload.direction === 'outgoing') {
        return;
      }
      setCards(current => {
        const match = current.find(card => payload.call_ref && card.callId === payload.call_ref) || current.find(card => phoneKey(card.callerPhone) === phoneKey(payload.caller_phone));
        if (match) {
          return current.map(card => card === match ? {
            ...card,
            customerId: payload.customer_id || card.customerId,
            callerName: payload.caller_name || card.callerName
          } : card);
        }
        const key = event?.at ? `event-${event.at}-${payload.voice_call_id}` : `event-${payload.voice_call_id || Date.now()}`;
        timers.current[key] = setTimeout(() => removeCard(key), EVENT_CARD_MS);
        return [...current, {
          key,
          callId: null,
          callerPhone: payload.caller_phone,
          callerName: payload.caller_name,
          customerId: payload.customer_id || null
        }];
      });
    });
  }, [subscribe, asCompany?.id, removeCard]);

  // Fetch each caller's snapshot once, however many cards show them, and forget it once no
  // card does - so the next call from them shows what's true then, not what was cached.
  useEffect(() => {
    const showing = new Set(cards.map(card => card.customerId).filter(Boolean));
    showing.forEach(customerId => {
      if (requested.current.has(customerId)) {
        return;
      }
      requested.current.add(customerId);
      setSnapshots(current => ({
        ...current,
        [customerId]: {
          loading: true
        }
      }));
      getCustomerCallSnapshot(asCompany?.id, {
        customer: customerId
      }).then(({
        data
      }) => setSnapshots(current => ({
        ...current,
        [customerId]: {
          data
        }
      }))).catch(e => {
        console.error('Call pop snapshot failed', e);
        // The card still shows who is calling; it just doesn't get the history.
        setSnapshots(current => ({
          ...current,
          [customerId]: {
            failed: true
          }
        }));
      });
    });
    requested.current.forEach(customerId => {
      if (!showing.has(customerId)) {
        requested.current.delete(customerId);
        setSnapshots(({
          [customerId]: _,
          ...rest
        }) => rest);
      }
    });
  }, [cards, asCompany?.id]);

  // A card can have no customer: the softphone rings before the backend's event arrives, and
  // the event never comes for a user who has call notifications turned off. Look those
  // callers up by number instead, once per number.
  useEffect(() => {
    const waiting = new Set(cards.filter(card => !card.customerId && phoneKey(card.callerPhone)).map(card => phoneKey(card.callerPhone)));
    waiting.forEach(key => {
      if (lookedUp.current.has(key)) {
        return;
      }
      lookedUp.current.add(key);
      setPhoneLookups(current => ({
        ...current,
        [key]: true
      }));
      const phone = cards.find(card => phoneKey(card.callerPhone) === key).callerPhone;
      getCustomerCallSnapshot(asCompany?.id, {
        phone
      }).then(({
        data
      }) => {
        if (!data) {
          return;
        }

        // Stored as if fetched by id, so the snapshot effect doesn't fetch it again.
        requested.current.add(data.id);
        setSnapshots(current => ({
          ...current,
          [data.id]: {
            data
          }
        }));
        // An event that already named a customer wins: it is the webhook's own match.
        setCards(current => current.map(card => !card.customerId && phoneKey(card.callerPhone) === key ? {
          ...card,
          customerId: data.id
        } : card));
      }).catch(e => console.error('Call pop phone lookup failed', e)).finally(() => setPhoneLookups(({
        [key]: _,
        ...rest
      }) => rest));
    });

    // Forget numbers no card is showing, so their next call is looked up afresh.
    const showing = new Set(cards.map(card => phoneKey(card.callerPhone)));
    lookedUp.current.forEach(key => {
      if (!showing.has(key)) lookedUp.current.delete(key);
    });
  }, [cards, asCompany?.id]);
  if (!cards.length) {
    return null;
  }
  return <Stack spacing={1.5} sx={{
    position: 'fixed',
    bottom: '88px',
    // Beside the softphone panel when it's open, rather than on top of it.
    right: softphoneOpen ? '400px' : '24px',
    zIndex: Z_INDEX,
    maxHeight: 'calc(100vh - 112px)',
    overflowY: 'auto'
  }}>
      {[...cards].reverse().map(card => {
      const snapshot = card.customerId ? snapshots[card.customerId] : null;
      const ringingHere = !!card.callId;
      return <CallPop key={card.key} callerPhone={card.callerPhone} callerName={card.callerName} snapshot={snapshot?.data} loading={!!snapshot?.loading || !card.customerId && !!phoneLookups[phoneKey(card.callerPhone)]} onAnswer={ringingHere ? () => {
        answerCall(card.callId);
        removeCard(card.key);
        // So the in-call controls are on screen once it connects.
        openSoftphone();
      } : undefined} onDetails={card.customerId ? () => {
        openCustomerDrawer(card.customerId, 'summary');
        // A ringing card stays, since it's still the way to answer.
        if (!ringingHere) removeCard(card.key);
      } : undefined}
      // The SDK's own ringing screen, which offered decline, is gone - so on a ringing
      // call the X is the decline. Just hiding the card would leave no way to answer.
      closeLabel={ringingHere ? 'Decline call' : 'Dismiss'} onClose={() => {
        if (ringingHere) declineCall(card.callId);
        removeCard(card.key);
      }} />;
    })}
    </Stack>;
};
export default CallPopHost;